/**
 * DOM utility functions for working with contentEditable elements and ranges
 */

import {EMPTY_PARAGRAPH} from '../hooks/useParagraphEditor';

export function getTextContent(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || '';
}

export function isContentEmpty(content: string): boolean {
    const textContent = getTextContent(content);
    return textContent.trim() === '' || textContent.trim() === EMPTY_PARAGRAPH;
}

function walkTextAndVariableSpans(
    root: Node,
    cb: (node: Node, isVariableSpan: boolean, textLength: number) => boolean
): void {
    if(root.nodeType === Node.TEXT_NODE) {
        const len = root.textContent?.length ?? 0;
        if(cb(root, false, len)) return;
        return;
    }
    if(root.nodeType === Node.ELEMENT_NODE && (root as Element).getAttribute?.('data-label')) {
        if(cb(root, true, 1)) return;
        return;
    }
    for(let i = 0; i < root.childNodes.length; i++) {
        walkTextAndVariableSpans(root.childNodes[i], cb);
    }
}

/**
 * Get character offset of the current selection/cursor within an element.
 * Counts text in tree order; variable spans (data-label) count as 1 for placement.
 */
export function getCursorOffsetInElement(element: HTMLElement): number {
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    if(!range?.startContainer || !element.contains(range.startContainer as Node)) {
        return 0;
    }
    let offset = 0;
    walkTextAndVariableSpans(element, (node, isVariableSpan, len) => {
        if(isVariableSpan) {
            if(node === range.startContainer || (node as Node).contains(range.startContainer)) {
                return true;
            }
            offset += 1;
        } else {
            if(node === range.startContainer) {
                offset += Math.min(range.startOffset, len);
                return true;
            }
            offset += len;
        }
        return false;
    });
    return offset;
}

/**
 * Create a collapsed range at the given character offset within the element.
 */
export function createRangeAtOffset(element: HTMLElement, characterOffset: number): Range {
    const range = document.createRange();
    let passed = 0;
    let done = false;
    walkTextAndVariableSpans(element, (node, isVariableSpan, len) => {
        if(isVariableSpan) {
            if(passed === characterOffset) {
                range.setStartBefore(node);
                range.collapse(true);
                done = true;
                return true;
            }
            passed += 1;
        } else {
            if(passed + len >= characterOffset) {
                range.setStart(node, Math.min(characterOffset - passed, len));
                range.collapse(true);
                done = true;
                return true;
            }
            passed += len;
        }
        return false;
    });
    if(done) return range;
    const lastChild = element.lastChild;
    if(lastChild?.nodeType === Node.TEXT_NODE) {
        range.setStart(lastChild, lastChild.textContent?.length || 0);
    } else if(lastChild) {
        range.setStartAfter(lastChild);
    } else {
        range.selectNodeContents(element);
    }
    range.collapse(true);
    return range;
}

/**
 * Get range within element (current selection, saved offset, or end of element)
 */
export function getRangeInElement(element: HTMLElement, savedCursorOffset?: number | null): Range {
    const selection = window.getSelection();
    let range = selection?.rangeCount ? selection.getRangeAt(0) : null;

    if(range?.startContainer && element.contains(range.startContainer as Node)) {
        return range;
    }

    if(typeof savedCursorOffset === 'number' && savedCursorOffset >= 0) {
        return createRangeAtOffset(element, savedCursorOffset);
    }

    element.focus();
    range = document.createRange();
    const lastChild = element.lastChild;

    if(lastChild?.nodeType === Node.TEXT_NODE) {
        range.setStart(lastChild, lastChild.textContent?.length || 0);
    } else if(lastChild) {
        range.setStartAfter(lastChild);
    } else {
        range.selectNodeContents(element);
    }
    range.collapse(true);
    return range;
}

/**
 * Get text content from the start of the element up to the range position.
 * Works whether range.startContainer is a text node or an element.
 */
function getTextBeforeRange(element: HTMLElement, range: Range): string {
    if(range.startContainer.nodeType === Node.TEXT_NODE && element.contains(range.startContainer)) {
        return range.startContainer.textContent?.substring(0, range.startOffset) || '';
    }
    const beforeRange = document.createRange();
    beforeRange.selectNodeContents(element);
    beforeRange.setEnd(range.startContainer, range.startOffset);
    return beforeRange.toString();
}

/**
 * Insert node with spacing
 * Automatically adds spaces before and after the inserted node if needed
 */
export function insertWithSpacing(
    element: HTMLElement,
    range: Range,
    node: Node,
    selection: Selection
): void {
    const textBefore = getTextBeforeRange(element, range);
    const needsSpaceBefore = textBefore.length > 0 && !textBefore.match(/\s$/);

    if(needsSpaceBefore) {
        range.insertNode(document.createTextNode(' '));
        range.setStartAfter(range.startContainer);
    }

    range.insertNode(node);
    range.setStartAfter(node);
    range.insertNode(document.createTextNode(' '));
    range.setStartAfter(range.startContainer);
    range.collapse(true);

    selection.removeAllRanges();
    selection.addRange(range);
}
